<template>
  <tr class="drive-file-row" @dblclick="onDblClick">
    <td class="drive-file-icon">
      <i :class="fileIconInfo.iconClass" :style="{ color: fileIconInfo.color }" aria-hidden="true"></i>
    </td>
    <td class="drive-file-name">
      <a
        v-if="file.isFolder"
        href="javascript: void(0)"
        @click="$emit('open-folder', file)"
      >
        {{ file.name }}
      </a>
      <span v-else>{{ file.name }}</span>
    </td>
    <td class="drive-file-size">
      {{ file.isFolder ? "--" : formatSize(file.size) }}
    </td>
    <td class="drive-file-created-by">
      {{ file.createdBy || '--' }}
    </td>
    <td class="drive-file-created">
      {{ formatDate(file.createdTime) }}
    </td>
    <td class="drive-file-modified">
      <span v-if="file.lastModifiedBy" class="drive-modified-by">{{ file.lastModifiedBy }}</span>
      <span class="drive-modified-date">{{ formatDate(file.modifiedTime) }}</span>
    </td>
    <td class="drive-file-actions">
      <el-tooltip v-if="!file.isFolder && file.webViewLink && isNativeGoogleDoc" content="Edit" placement="top" effect="dark">
        <a :href="file.webViewLink" target="_blank" rel="noopener noreferrer" @click.stop>
          <i class="fa fa-pencil-square-o" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="!file.isFolder && isEditableTextFile" content="Edit" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('edit-text-file', file)">
          <i class="fa fa-pencil-square-o" aria-hidden="true" style="color: #67c23a;"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="!file.isFolder" content="Preview" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('preview-file', file)">
          <i class="fa fa-eye" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" :content="file.visibilityRestricted ? 'Hidden from non-admins — click to show' : 'Visible to all — click to hide'" placement="top" effect="dark">
        <a href="javascript: void(0)" :class="file.visibilityRestricted ? 'drive-visibility-badge' : ''" @click="$emit('toggle-visibility', file)">
          <i :class="file.visibilityRestricted ? 'fa fa-eye-slash' : 'fa fa-eye'" :style="{ color: file.visibilityRestricted ? '#f56c6c' : '#ccc' }" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="!file.isFolder && (!file.downloadRestricted || isAdmin)" content="Download" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('download-file', file)">
          <i class="fa fa-cloud-download" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="file.deadline" :content="deadlineTooltip" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="onDeadlineClick">
          <i class="fa fa-calendar-times-o" :style="{ color: deadlineColor }" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin && !file.deadline && !file.isFolder" content="Set deadline" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('set-deadline', file)">
          <i class="fa fa-calendar-plus-o" style="color: #ccc;" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" :content="file.downloadRestricted ? 'Remove download restriction' : 'Restrict downloads'" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('toggle-restriction', file)">
          <i :class="file.downloadRestricted ? 'fa fa-lock' : 'fa fa-unlock-alt'" :style="{ color: file.downloadRestricted ? '#e6a23c' : '' }" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" content="Share" placement="top" effect="dark">
        <a href="javascript: void(0)" class="drive-action-share" @click="$emit('share-file', file)">
          <i class="fa fa-share-alt" aria-hidden="true"></i>
          <span v-if="file.permissionCount > 0" class="drive-share-badge">{{ file.permissionCount }}</span>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" content="Rename" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('rename-file', file)">
          <i class="fa fa-pencil" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" content="Move" placement="top" effect="dark">
        <a href="javascript: void(0)" @click="$emit('move-file', file)">
          <i class="fa fa-arrows" aria-hidden="true"></i>
        </a>
      </el-tooltip>
      <el-tooltip v-if="isAdmin" content="Delete" placement="top" effect="dark">
        <a href="javascript: void(0)" class="drive-action-delete" @click="$emit('delete-file', file)">
          <i class="fa fa-trash-o" aria-hidden="true"></i>
        </a>
      </el-tooltip>
    </td>
  </tr>
</template>

<script>
import { getFileIconInfo } from "./driveIcons";

export default {
  name: "DriveFileRow",
  props: {
    file: { type: Object, required: true },
    isAdmin: { type: Boolean, default: false },
  },
  computed: {
    fileIconInfo: function () {
      return getFileIconInfo(this.file);
    },
    isNativeGoogleDoc: function () {
      var mime = (this.file.mimeType || "");
      return mime.indexOf("application/vnd.google-apps.") === 0 && !this.file.isFolder;
    },
    isEditableTextFile: function () {
      var mime = (this.file.mimeType || "").toLowerCase();
      var editableMimes = [
        "text/plain", "text/markdown", "text/html", "text/csv", "text/css",
        "application/json", "application/xml", "application/javascript",
      ];
      if (editableMimes.indexOf(mime) !== -1) return true;
      var ext = (this.file.fileExtension || this.file.name.split(".").pop() || "").toLowerCase();
      var editableExts = ["txt", "md", "log", "json", "csv", "xml", "html", "css", "js"];
      return editableExts.indexOf(ext) !== -1;
    },
    deadlineColor: function () {
      if (!this.file.deadline) return "#ccc";
      var days = this.file.deadline.daysRemaining;
      if (days <= 0) return "#c0392b";
      if (days <= 3) return "#e67e22";
      if (days <= 7) return "#f1c40f";
      return "#27ae60";
    },
    deadlineTooltip: function () {
      if (!this.file.deadline) return "";
      var dl = this.file.deadline;
      var text = "Deadline: " + dl.date;
      if (dl.label) text += " — " + dl.label;
      if (dl.daysRemaining <= 0) {
        text += " (Due today!)";
      } else {
        text += " (" + dl.daysRemaining + " day" + (dl.daysRemaining > 1 ? "s" : "") + " remaining)";
      }
      return text;
    },
  },
  methods: {
    formatSize: function (bytes) {
      if (!bytes || bytes === 0) return "--";
      var units = ["B", "KB", "MB", "GB"];
      var i = 0;
      var size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return size.toFixed(i > 0 ? 1 : 0) + " " + units[i];
    },
    formatDate: function (dateStr) {
      if (!dateStr) return "--";
      var d = new Date(dateStr);
      return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    onDeadlineClick: function () {
      if (this.isAdmin) {
        this.$emit("set-deadline", this.file);
      }
    },
    onDblClick: function () {
      if (this.file.isFolder) {
        this.$emit("open-folder", this.file);
      } else {
        this.$emit("preview-file", this.file);
      }
    },
  },
};
</script>

<style scoped>
.drive-file-row {
  cursor: pointer;
}
.drive-file-row:hover {
  background: #f5f7fa;
}
.drive-file-icon {
  text-align: center;
  width: 40px;
}
.drive-file-name a {
  color: #337ab7;
  font-weight: 500;
}
.drive-file-created-by {
  font-size: 12px;
  color: #333;
  font-weight: 500;
}
.drive-file-created,
.drive-file-modified {
  font-size: 12px;
  color: #666;
}
.drive-modified-by {
  display: block;
  font-weight: 500;
  color: #333;
}
.drive-modified-date {
  display: block;
}
.drive-file-actions a {
  margin-right: 10px;
  color: #666;
  font-size: 14px;
}
.drive-file-actions a:hover {
  color: #337ab7;
}
.drive-file-actions .drive-action-delete:hover {
  color: #c0392b;
}
.drive-restriction-badge {
  color: #e6a23c;
  font-size: 13px;
  margin-right: 10px;
}
.drive-visibility-badge {
  color: #f56c6c;
  font-size: 13px;
  margin-right: 10px;
}
.drive-action-share {
  position: relative;
}
.drive-share-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #409eff;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 8px;
  min-width: 14px;
  text-align: center;
}
</style>
